import type { FormikErrors, FormikProps } from 'formik'

import { FlowPage } from '@monthio/design-system'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import type { QuestionDefinition } from '../../../CustomQuestionnaire'
import type {
	DefaultConsumerInputs,
	QuestionnaireStep,
	QuestionnaireStepValidationParameters,
} from '../../types/questionnaire.types'

import {
	QuestionComponent,
	validateAgainstQuestion,
} from '../../../CustomQuestionnaire'
import { getFixedExpensesSplitQuestion } from './FixedExpensesSplitQuestion'

const HouseholdSingleCohabitantQuestionnaireForm = ({
	formProps,
}: {
	formProps: FormikProps<DefaultConsumerInputs>
}) => {
	const question: QuestionDefinition = getFixedExpensesSplitQuestion()
	const { t } = useTranslation()

	useEffect(() => {
		formProps.setSubmitting(false)
		formProps.setTouched({
			...formProps.touched,
			householdSingleCohabitantInformation: undefined,
		})
	}, [])

	return (
		<FlowPage.Content $spacing="xl">
			<FlowPage.Header
				title={t('consumerInputs-householdSingleCohabitant-title')}
			/>
			<QuestionComponent
				key={question.id}
				formProps={formProps}
				question={question}
				formikFieldPrefix="householdSingleCohabitantInformation"
			/>
		</FlowPage.Content>
	)
}

const HouseholdSingleCohabitantQuestionnaireFormValidation = async ({
	values,
	errors,
}: QuestionnaireStepValidationParameters): Promise<
	FormikErrors<DefaultConsumerInputs>
> => {
	const mainQuestion = getFixedExpensesSplitQuestion()

	validateAgainstQuestion(
		values.householdSingleCohabitantInformation,
		mainQuestion,
		errors,
	)

	if (errors.answers) {
		Object.keys(errors.answers).forEach((key) => {
			const error = errors.answers[key]
			if (error) {
				delete errors.answers[key]
				errors.householdSingleCohabitantInformation = {
					...errors.householdSingleCohabitantInformation,
					answers: {
						...errors.householdSingleCohabitantInformation?.answers,
						[key]: error,
					},
				}
			}
		})

		if (Object.keys(errors.answers).length === 0) {
			delete errors.answers
		}
	}

	return errors
}

export const HouseholdSingleCohabitantQuestionnaire: QuestionnaireStep = {
	validation: HouseholdSingleCohabitantQuestionnaireFormValidation,
	component: HouseholdSingleCohabitantQuestionnaireForm,
}
